import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Truck, Calendar, MapPin, Package, User, Mail, Phone, Home, Edit3, Camera, Settings, Bell, Shield, LogOut } from "lucide-react";

const ordersData = [
  {
    id: 1,
    title: "Grèce 2024",
    image: "/images/occasions/voyage1.jpg",
    orderDate: "18/10/2024 09:54",
    deliveryDate: "21/10/2024 09:45",
    estimatedDelivery: "25/10/2024 et le 29/10/2024",
    status: "Album commandé",
    deliveryStatus: "Album livré",
    quantity: 1,
    total: "0.0€",
    description: "Album rigide • Pix A4 • 24 pages",
    deliveryAddress: {
      name: "Hubert Dalstein",
      address: "198 avenue de France",
      city: "75014 paris France"
    }
  },
  {
    id: 2,
    title: "Naissance (copie)",
    image: "/images/occasions/naissance1.jpg",
    orderDate: "25/09/2024 11:14",
    deliveryDate: "14/10/2024 17:02",
    estimatedDelivery: "02/10/2024 et le 06/10/2024",
    status: "Album commandé",
    deliveryStatus: "Album livré",
    quantity: 1,
    total: "0.0€",
    description: "Album rigide • Pix A4 • 32 pages",
    deliveryAddress: {
      name: "Hubert Dalstein",
      address: "198 avenue de France",
      city: "75014 Paris France"
    }
  }
];

const profileData = {
  name: "Hubert Dalstein",
  firstName: "Hubert",
  lastName: "Dalstein",
  email: "hubert@example.com",
  phone: "+33 1 23 45 67 89",
  address: "198 avenue de France",
  city: "75014 Paris",
  country: "France",
  joinDate: "Membre depuis septembre 2024",
  avatar: "/images/team/hubert.png",
  totalOrders: 15,
  favoriteProducts: ["Albums Voyage", "Albums Famille"],
  membershipLevel: "Premium"
};

const promoCodesData = [
  {
    code: "WELCOME10",
    description: "10% de réduction sur votre première commande",
    status: "Utilisé",
    usedDate: "18/10/2024"
  },
  {
    code: "SUMMER2024",
    description: "15% de réduction sur tous les albums",
    status: "Expiré",
    expiryDate: "31/08/2024"
  }
];

export default function Commandes() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-accent/10">
      <Navigation />
      
      <main className="container mx-auto px-4 pt-32 pb-16">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold text-center mb-8 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Mon compte
          </h1>
          
          <Tabs defaultValue="orders" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="profile">Mon profil</TabsTrigger>
              <TabsTrigger value="orders">Mes commandes</TabsTrigger>
              <TabsTrigger value="promos">Mes codes promo</TabsTrigger>
            </TabsList>
            
            <TabsContent value="profile" className="space-y-6">
              {/* Header Profile Card */}
              <Card className="backdrop-blur-sm bg-gradient-to-br from-card/90 to-accent/20 border-primary/10 shadow-elegant">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    {/* Avatar Section */}
                    <div className="relative">
                      <Avatar className="w-32 h-32 border-4 border-primary/20 shadow-lg">
                        <AvatarImage src={profileData.avatar} alt={profileData.name} />
                        <AvatarFallback className="text-2xl font-bold bg-gradient-to-br from-primary to-secondary text-white">
                          {profileData.firstName[0]}{profileData.lastName[0]}
                        </AvatarFallback>
                      </Avatar>
                      <Button 
                        size="icon" 
                        variant="secondary" 
                        className="absolute -bottom-2 -right-2 rounded-full w-10 h-10 shadow-lg"
                      >
                        <Camera className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Profile Info */}
                    <div className="flex-1 text-center md:text-left">
                      <h2 className="text-3xl font-bold text-foreground mb-2">{profileData.name}</h2>
                      <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
                        <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                          {profileData.membershipLevel}
                        </Badge>
                        <p className="text-muted-foreground">{profileData.joinDate}</p>
                      </div>
                      <div className="flex flex-col md:flex-row items-center gap-6 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-primary" />
                          <span>{profileData.totalOrders} commandes</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-primary" />
                          <span>{profileData.email}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2">
                      <Button variant="outline" size="sm" className="gap-2">
                        <Edit3 className="h-4 w-4" />
                        Modifier le profil
                      </Button>
                      <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground">
                        <Settings className="h-4 w-4" />
                        Paramètres
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Information Cards Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <Card className="backdrop-blur-sm bg-card/80 border-border/50 hover:shadow-card transition-smooth">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-xl flex items-center gap-2">
                      <User className="h-5 w-5 text-primary" />
                      Informations personnelles
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                        <Mail className="h-4 w-4 text-primary" />
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Email</label>
                          <p className="text-foreground font-medium">{profileData.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                        <Phone className="h-4 w-4 text-primary" />
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Téléphone</label>
                          <p className="text-foreground font-medium">{profileData.phone}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Address Information */}
                <Card className="backdrop-blur-sm bg-card/80 border-border/50 hover:shadow-card transition-smooth">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-xl flex items-center gap-2">
                      <Home className="h-5 w-5 text-primary" />
                      Adresse de livraison
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 rounded-lg bg-gradient-to-br from-muted/20 to-accent/10 border border-border/50">
                      <div className="space-y-2">
                        <p className="font-medium text-foreground">{profileData.name}</p>
                        <p className="text-muted-foreground">{profileData.address}</p>
                        <p className="text-muted-foreground">{profileData.city}, {profileData.country}</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="w-full gap-2">
                      <MapPin className="h-4 w-4" />
                      Modifier l'adresse
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Preferences & Actions */}
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="backdrop-blur-sm bg-card/80 border-border/50 hover:shadow-card transition-smooth">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Bell className="h-5 w-5 text-primary" />
                      Notifications
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" size="sm" className="w-full">
                      Gérer
                    </Button>
                  </CardContent>
                </Card>

                <Card className="backdrop-blur-sm bg-card/80 border-border/50 hover:shadow-card transition-smooth">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      Sécurité
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" size="sm" className="w-full">
                      Changer mot de passe
                    </Button>
                  </CardContent>
                </Card>

                <Card className="backdrop-blur-sm bg-card/80 border-border/50 hover:shadow-card transition-smooth">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <LogOut className="h-5 w-5 text-destructive" />
                      Session
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Button variant="destructive" size="sm" className="w-full">
                      Se déconnecter
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="orders" className="space-y-6">
              {ordersData.map((order) => (
                <Card key={order.id} className="backdrop-blur-sm bg-card/80 border-border/50">
                  <CardContent className="p-6">
                    <div className="grid lg:grid-cols-3 gap-6">
                      {/* Image et détails de la commande */}
                      <div className="lg:col-span-1">
                        <h3 className="text-2xl font-bold text-primary mb-4">{order.title}</h3>
                        <img 
                          src={order.image} 
                          alt={order.title}
                          className="w-full h-48 object-cover rounded-lg shadow-sm mb-4"
                        />
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium">Quantité :</span> {order.quantity}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium">Total :</span> {order.total}
                          </p>
                          <p className="text-sm text-muted-foreground">{order.description}</p>
                        </div>
                      </div>
                      
                      {/* Adresse de livraison */}
                      <div className="lg:col-span-1">
                        <div className="flex items-center gap-2 mb-4">
                          <Truck className="h-5 w-5 text-primary" />
                          <h4 className="font-semibold text-foreground">Adresse de livraison</h4>
                        </div>
                        <div className="space-y-2 mb-6">
                          <p className="font-medium text-foreground">{order.deliveryAddress.name}</p>
                          <p className="text-sm text-muted-foreground">{order.deliveryAddress.address}</p>
                          <p className="text-sm text-muted-foreground">{order.deliveryAddress.city}</p>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-primary" />
                            <span className="text-sm text-muted-foreground">
                              {order.orderDate} : {order.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-muted-foreground">
                              {order.deliveryDate} : {order.deliveryStatus}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <span className="font-medium">Date de livraison estimée entre le :</span>
                            <br />
                            {order.estimatedDelivery}
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="lg:col-span-1 flex flex-col justify-center space-y-3">
                        <Button variant="default" className="w-full">
                          Voir mon album
                        </Button>
                        <Button variant="outline" className="w-full">
                          Renouveler ma commande
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
            
            <TabsContent value="promos" className="space-y-6">
              <Card className="backdrop-blur-sm bg-card/80 border-border/50">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-semibold mb-6 text-foreground">Mes codes promo</h2>
                  <div className="space-y-4">
                    {promoCodesData.map((promo, index) => (
                      <div key={index} className="flex items-center justify-between p-4 border border-border rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <code className="font-mono font-bold text-primary bg-primary/10 px-2 py-1 rounded">
                              {promo.code}
                            </code>
                            <Badge variant={promo.status === "Utilisé" ? "secondary" : "destructive"}>
                              {promo.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">{promo.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {promo.status === "Utilisé" ? `Utilisé le ${promo.usedDate}` : `Expiré le ${promo.expiryDate}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}